import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const SponsorsEditor = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentSponsor, setCurrentSponsor] = useState({
    id: null,
    logo_path: '',
    website_url: '',
    display_order: 0,
    is_active: true,
    translations: {
      en: { name: '' },
      cs: { name: '' },
    },
  });
  const [selectedLogo, setSelectedLogo] = useState(null);
  const language = getUserLanguage();

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'accept-language': language };
      const data = await get('/sponsors', headers);
      const processedData = (data || []).map(sponsor => ({
        ...sponsor,
        is_active: !!sponsor.is_active,
        display_order: sponsor.display_order || 0,
        translations: sponsor.translations || {
          en: { name: '' },
          cs: { name: '' },
        },
      }));
      setSponsors(processedData);
    } catch (err) {
      console.error('[SponsorsEditor] Error fetching sponsors:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst sponzory' : 'Failed to load sponsors');
      setSponsors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang = language) => {
    const { name, value, type, checked } = e.target;
    if (name === 'name') {
      setCurrentSponsor(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            name: value,
          },
        },
      }));
    } else {
      setCurrentSponsor(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedLogo(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentSponsor.translations.en.name || !currentSponsor.translations.cs.name) {
      setError(language === 'cs' ? 'Jméno je povinné v obou jazycích' : 'Name is required in both languages');
      return;
    }

    try {
      const formData = new FormData();
      if (selectedLogo) {
        formData.append('logo', selectedLogo);
      } else if (editMode && currentSponsor.logo_path) {
        formData.append('logo_path', currentSponsor.logo_path);
      }
      formData.append('website_url', currentSponsor.website_url);
      formData.append('display_order', currentSponsor.display_order || 0);
      formData.append('is_active', currentSponsor.is_active ? 'true' : 'false');
      formData.append('translations', JSON.stringify(currentSponsor.translations));

      let response;
      if (editMode) {
        response = await put(`/sponsors/${currentSponsor.id}`, formData);
        setSuccess(language === 'cs' ? 'Sponzor úspěšně aktualizován' : 'Sponsor updated successfully');
      } else {
        response = await post('/sponsors', formData);
        setSuccess(language === 'cs' ? 'Sponzor úspěšně přidán' : 'Sponsor added successfully');
      }

      resetForm();
      fetchSponsors();
    } catch (err) {
      console.error('[SponsorsEditor] Error saving sponsor:', err);
      setError(language === 'cs' ? `Nepodařilo se uložit sponzora: ${err.message}` : `Failed to save sponsor: ${err.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      const sponsor = await get(`/sponsors/${id}`);
      setCurrentSponsor({
        id: sponsor.id,
        logo_path: sponsor.logo_path || '',
        website_url: sponsor.website_url || '',
        display_order: sponsor.display_order || 0,
        is_active: !!sponsor.is_active,
        translations: sponsor.translations || {
          en: { name: '' },
          cs: { name: '' },
        },
      });
      setEditMode(true);
      setSelectedLogo(null);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[SponsorsEditor] Error fetching sponsor for edit:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst sponzora pro úpravu' : 'Failed to load sponsor for editing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tohoto sponzora?' : 'Are you sure you want to delete this sponsor?')) {
      return;
    }
    try {
      await del(`/sponsors/${id}`);
      setSuccess(language === 'cs' ? 'Sponzor úspěšně smazán' : 'Sponsor deleted successfully');
      fetchSponsors();
    } catch (err) {
      console.error('[SponsorsEditor] Error deleting sponsor:', err);
      setError(language === 'cs' ? 'Nepodařilo se smazat sponzora' : 'Failed to delete sponsor');
    }
  };

  const resetForm = () => {
    setCurrentSponsor({
      id: null,
      logo_path: '',
      website_url: '',
      display_order: 0,
      is_active: true,
      translations: {
        en: { name: '' },
        cs: { name: '' },
      },
    });
    setSelectedLogo(null);
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa sponzorů' : 'Sponsors Management'}</h1>
        <button
          onClick={handleAddNew}
          className="admin-btn admin-btn-primary"
        >
          {language === 'cs' ? 'Přidat sponzora' : 'Add Sponsor'}
        </button>
      </div>

      {error && (
        <div className="admin-alert admin-alert-error">{error}</div>
      )}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode ? (language === 'cs' ? 'Upravit sponzora' : 'Edit Sponsor') : (language === 'cs' ? 'Přidat sponzora' : 'Add Sponsor')}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="admin-form-input"
                      value={currentSponsor.translations.en.name || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Jméno</label>
                    <input
                      type="text"
                      name="name"
                      className="admin-form-input"
                      value={currentSponsor.translations.cs.name || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Logo' : 'Logo'}</label>
                    <input
                      type="file"
                      name="logo"
                      className="admin-form-input"
                      onChange={handleLogoChange}
                      accept="image/*"
                    />
                    {editMode && !selectedLogo && currentSponsor.logo_path && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Aktuální logo:' : 'Current logo:'}</p>
                        <img
                          src={`${IMAGE_BASE_URL}${currentSponsor.logo_path}`}
                          alt={currentSponsor.translations[language]?.name || 'Sponsor'}
                          className="admin-mt-2 admin-h-32 admin-object-contain admin-rounded"
                          onError={(e) => {
                            console.error(`Failed to load image: ${currentSponsor.logo_path}`);
                            e.target.onerror = null;
                            e.target.src = '/images/sponsors/placeholder.png';
                          }}
                        />
                      </div>
                    )}
                    {selectedLogo && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Vybrané logo:' : 'Selected logo:'} {selectedLogo.name}</p>
                        <img
                          src={URL.createObjectURL(selectedLogo)}
                          alt="Preview"
                          className="admin-mt-2 admin-h-32 admin-object-contain admin-rounded"
                        />
                      </div>
                    )}
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'URL webu' : 'Website URL'}</label>
                    <input
                      type="url"
                      name="website_url"
                      className="admin-form-input"
                      value={currentSponsor.website_url || ''}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                    <input
                      type="number"
                      name="display_order"
                      className="admin-form-input"
                      value={currentSponsor.display_order || 0}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={!!currentSponsor.is_active}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="admin-mt-6 admin-flex admin-items-center">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-mr-2"
                >
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={resetForm}
                >
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">{language === 'cs' ? 'Seznam sponzorů' : 'Sponsors List'}</div>
        {sponsors.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">{language === 'cs' ? 'Žádní sponzoři nebyli nalezeni.' : 'No sponsors found.'}</div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Jméno' : 'Name'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {sponsors.map((sponsor) => {
                  const sponsorData = sponsor.translations && sponsor.translations[language]
                    ? sponsor.translations[language]
                    : (sponsor.translations && sponsor.translations.en
                      ? sponsor.translations.en
                      : { name: '' });
                  return (
                    <tr key={sponsor.id} className="admin-table-row">
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-items-center">
                          <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                            <img
                              className="admin-h-10 admin-w-10 admin-rounded-full admin-object-contain"
                              src={sponsor.logo_path ? `${IMAGE_BASE_URL}${sponsor.logo_path}` : '/images/sponsors/placeholder.png'}
                              alt={sponsorData.name || 'Sponsor'}
                              onError={(e) => {
                                console.error(`Failed to load image: ${sponsor.logo_path}`);
                                e.target.onerror = null;
                                e.target.src = '/images/sponsors/placeholder.png';
                              }}
                            />
                          </div>
                          <div className="admin-ml-4">
                            <div className="admin-text-sm admin-font-medium admin-text-white">
                              {sponsorData.name || (language === 'cs' ? 'Bezejmenný sponzor' : 'Unnamed Sponsor')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{sponsor.display_order || 0}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span className={`admin-badge ${sponsor.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                          {sponsor.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                        <button onClick={() => handleEdit(sponsor.id)} className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3">
                          {language === 'cs' ? 'Upravit' : 'Edit'}
                        </button>
                        <button onClick={() => handleDelete(sponsor.id)} className="admin-text-red-600 hover:admin-text-red-900">
                          {language === 'cs' ? 'Smazat' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorsEditor;